import React from 'react'
import { SelectOptions } from '../Components/Selects'
import { DeviceResponse } from '../../ConfigureReport/FilterTab/Types'
import { SelectOption } from '../Components/Selects/Types'
import { AgentResponse } from '../../../NonReact/Types/Types'
import { displayError } from '../Utils/Messages'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE } from './ItemsGetters'
import { getAgents, getDevices, getGroupMembers } from '../../../NonReact/common/API/EntityAPIClient'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { ALL_VALUE_STRING_DEFAULT } from '../../../NonReact/common/DefaultValues'
import { GroupMembershipResponse } from '../Types/Types'
import { reduceGroupMemberships } from '../../../NonReact/common/API/APIMappers'

export enum SearchableEntities {
    DEVICES,
    AGENTS
}

export type EntityLocationInfo = {
    telSys: number
    partitions: number[]
}

const ENTITY_SEARCH_CACHE_TIME: number = 10000

/**
 * Search for entities across partitions
 *
 * @param entity - The entity type to search for
 * @param telSys - The telSys ID
 * @param partitions - The partitions to search across
 * @param query - The search query
 * @param deviceTypeFiltering - The device type filtering
 * @param groups - Optional groups to filter the search
 *
 * @returns A promise that resolves to a list of select options
 */
export const searchEntitiesAcrossPartitions = (
    entity: SearchableEntities,
    { telSys, partitions }: EntityLocationInfo,
    query: string,
    deviceTypeFiltering: Definable<number[]>,
    groups?: string[]
): Promise<SelectOptions<string>> => {
    let entityPromises: Promise<SelectOptions<string>>[] = []

    const processDeviceTypeFiltering = (deviceFiltering: Definable<number[]>): Definable<string[]> => {
        if (!isVarNotEmpty(deviceFiltering)) return undefined
        if (deviceFiltering.length === 1 && deviceFiltering[0] === ALL_VALUE) return undefined

        return deviceFiltering.map((deviceType: Definable<number>): string => `${deviceType}`)
    }

    switch (entity) {
        case SearchableEntities.DEVICES:
            entityPromises = partitions.map(
                (partition: number): Promise<SelectOptions<string>> =>
                    (!ArrayUtils.isEmpty(groups) && groups?.[0] !== ALL_VALUE_STRING_DEFAULT
                        ? Promise.all(
                              groups.map(
                                  (scopeGroup: string): Promise<Nullable<GroupMembershipResponse>> =>
                                      getGroupMembers(
                                          telSys,
                                          partition,
                                          scopeGroup,
                                          processDeviceTypeFiltering(deviceTypeFiltering),
                                          query,
                                          undefined,
                                          ENTITY_SEARCH_CACHE_TIME,
                                          true
                                      )
                              )
                          )
                              .then(reduceGroupMemberships)
                              .then(
                                  (groupMembers: Nullable<GroupMembershipResponse>): DeviceResponse[] =>
                                      groupMembers?.Devices ?? []
                              )
                        : getDevices(
                              telSys,
                              partition,
                              true,
                              processDeviceTypeFiltering(deviceTypeFiltering),
                              query,
                              ENTITY_SEARCH_CACHE_TIME,
                              undefined,
                              true
                          )
                    ).then(
                        (devices: DeviceResponse[]): SelectOptions<string> =>
                            devices.map(
                                ({ Description, DeviceNumber }: DeviceResponse): SelectOption<string> => ({
                                    label: Description,
                                    value: DeviceNumber,
                                    renderOptionElement: (
                                        <div>
                                            {DeviceNumber} - {Description}
                                        </div>
                                    )
                                })
                            )
                    )
            )
            break
        case SearchableEntities.AGENTS:
            entityPromises = partitions.map(
                (partition: number): Promise<SelectOptions<string>> =>
                    (!ArrayUtils.isEmpty(groups) && groups?.[0] !== ALL_VALUE_STRING_DEFAULT
                        ? Promise.all(
                              groups.map(
                                  (scopeGroup: string): Promise<Nullable<GroupMembershipResponse>> =>
                                      getGroupMembers(
                                          telSys,
                                          partition,
                                          scopeGroup,
                                          processDeviceTypeFiltering(deviceTypeFiltering),
                                          query,
                                          undefined,
                                          ENTITY_SEARCH_CACHE_TIME
                                      )
                              )
                          )
                              .then(reduceGroupMemberships)
                              .then(
                                  (groupMembers: Nullable<GroupMembershipResponse>): AgentResponse[] =>
                                      groupMembers?.ACDAgents ?? []
                              )
                        : getAgents(telSys, partition, true, query, ENTITY_SEARCH_CACHE_TIME)
                    ).then(
                        (agents: AgentResponse[]): SelectOptions<string> =>
                            agents.map(
                                ({ Description, AgentNumber }: AgentResponse): SelectOption<string> => ({
                                    label: Description,
                                    value: AgentNumber,
                                    renderOptionElement: (
                                        <div>
                                            {AgentNumber} - {Description}
                                        </div>
                                    )
                                })
                            )
                    )
            )
            break
        default:
            break
    }

    return Promise.all(entityPromises)
        .then((entities: SelectOptions<string>[]): SelectOptions<string> => entities.flat())
        .catch((e: Error): SelectOptions<string> => {
            displayError(e)
            return []
        })
}
